// Bundle the vendored sanitizer with the chat UI. The template also loads it
// for legacy scripts, but v-html consumers must not become unsafe when that
// optional script tag is unavailable or loads out of order.
import "../../js/purify.min.js";

const bundledDOMPurify = (
  typeof globalThis !== 'undefined'
  && globalThis.DOMPurify
  && typeof globalThis.DOMPurify.sanitize === 'function'
) ? globalThis.DOMPurify : null;

const safePart = value => String(value == null ? '' : value).trim();
const provisionalAssistantMessageIdPattern = /^(?:client|msg)-assistant-[a-z0-9-]+$/i;

const isTrueFlag = value => (
  value === true
  || value === 1
  || safePart(value).toLowerCase() === 'true'
);

export const durableChatResponseId = value => {
  const responseId = safePart(value);
  return provisionalAssistantMessageIdPattern.test(responseId) ? '' : responseId;
};

export function escapePlaintextHtml(value) {
  return String(value == null ? '' : value)
    .replace(/[&<>"']/g, char => ({
      '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;',
    }[char]))
    .replace(/\r?\n/g, '<br>');
}

export function sanitizeRichHtml(html, plaintextFallback = '', options = {}) {
  if (!bundledDOMPurify) return escapePlaintextHtml(plaintextFallback);
  try {
    return bundledDOMPurify.sanitize(String(html == null ? '' : html), {
      USE_PROFILES: { html: true },
      ...(options && typeof options === 'object' ? options : {}),
    });
  } catch (error) {
    return escapePlaintextHtml(plaintextFallback);
  }
}

export function hasChatSequenceGap(expectedSequence, data) {
  if (!data || data.sequence === undefined || data.sequence === null) return false;
  return Number(data.sequence) !== Number(expectedSequence);
}

export function consumeChunkedRealtimeUpdate(
  state,
  data,
  expectedSequence,
  textField = 'text',
  { preserveSequenceSiblings = false } = {},
) {
  const chunkState = state && typeof state === 'object' ? state : {};
  if (!chunkState.groups || typeof chunkState.groups !== 'object') {
    chunkState.groups = {};
  }
  if (!Array.isArray(chunkState.completedIds)) {
    chunkState.completedIds = [];
  }

  if (!data || data.chunk_reassemble !== true) {
    if (data && data.recovery_poll === true) {
      chunkState.groups = {};
      return { status: 'complete', data };
    }
    if (Object.keys(chunkState.groups).length > 0) {
      chunkState.groups = {};
      return { status: 'gap', data: null };
    }
    return { status: 'complete', data };
  }

  const chunkId = String(data.chunk_id || '').trim();
  const chunkIndex = Number(data.chunk_index);
  const chunkTotal = Number(data.chunk_total);
  if (
    !chunkId
    || !Number.isInteger(chunkIndex)
    || !Number.isInteger(chunkTotal)
    || chunkTotal < 2
    || chunkTotal > 10000
    || chunkIndex < 0
    || chunkIndex >= chunkTotal
    || typeof data[textField] !== 'string'
  ) {
    chunkState.groups = {};
    return { status: 'invalid', data: null };
  }
  if (chunkState.completedIds.includes(chunkId)) {
    return { status: 'duplicate', data: null };
  }

  const hasSequence = data.sequence !== undefined && data.sequence !== null;
  if (hasSequence) {
    const incomingSequence = Number(data.sequence);
    const expected = Number(expectedSequence);
    if (!Number.isFinite(incomingSequence) || !Number.isFinite(expected)) {
      chunkState.groups = {};
      return { status: 'invalid', data: null };
    }
    if (incomingSequence < expected) {
      delete chunkState.groups[chunkId];
      return { status: 'duplicate', data: null };
    }
    if (incomingSequence > expected) {
      chunkState.groups = {};
      return { status: 'gap', data: null };
    }
  }

  let group = chunkState.groups[chunkId];
  const sequenceKey = hasSequence ? String(data.sequence) : '';
  if (!group) {
    if (Object.keys(chunkState.groups).length >= 16) {
      delete chunkState.groups[Object.keys(chunkState.groups)[0]];
    }
    group = {
      total: chunkTotal,
      sequenceKey,
      fragments: new Array(chunkTotal),
      received: 0,
    };
    chunkState.groups[chunkId] = group;
  } else if (
    group.total !== chunkTotal
    || group.sequenceKey !== sequenceKey
  ) {
    delete chunkState.groups[chunkId];
    return { status: 'invalid', data: null };
  }

  if (!group.fragments[chunkIndex]) {
    group.received += 1;
  }
  group.fragments[chunkIndex] = { ...data };
  if (group.received < chunkTotal) {
    return { status: 'pending', data: null };
  }
  if (group.fragments.some(fragment => !fragment)) {
    return { status: 'pending', data: null };
  }

  const assembled = {
    ...group.fragments[0],
    [textField]: group.fragments
      .map(fragment => fragment[textField])
      .join(''),
  };
  delete assembled.chunk_id;
  delete assembled.chunk_index;
  delete assembled.chunk_total;
  delete assembled.chunk_reassemble;
  delete chunkState.groups[chunkId];
  if (sequenceKey && !preserveSequenceSiblings) {
    for (const [otherId, otherGroup] of Object.entries(chunkState.groups)) {
      if (otherGroup && otherGroup.sequenceKey === sequenceKey) {
        delete chunkState.groups[otherId];
      }
    }
  }
  chunkState.completedIds.push(chunkId);
  if (chunkState.completedIds.length > 32) {
    chunkState.completedIds.splice(0, chunkState.completedIds.length - 32);
  }
  return { status: 'complete', data: assembled };
}

if (typeof globalThis !== 'undefined') {
  globalThis.consumeChunkedRealtimeUpdate = consumeChunkedRealtimeUpdate;
}

export function normalizeChatRunId(runId) {
  if (runId === undefined || runId === null || runId === '') return null;
  return String(runId);
}

export function chatRunIdIsAtMost(candidateRunId, referenceRunId) {
  const candidate = normalizeChatRunId(candidateRunId);
  const reference = normalizeChatRunId(referenceRunId);
  if (!candidate || !reference) return false;
  if (candidate === reference) return true;
  const candidateNumeric = Number(candidate);
  const referenceNumeric = Number(reference);
  if (Number.isFinite(candidateNumeric) && Number.isFinite(referenceNumeric)) {
    return candidateNumeric <= referenceNumeric;
  }
  return false;
}

const COMPLETED_CHAT_TEXT_FINGERPRINT_MIN_CHARS = 32;

export function completedChatTextFingerprint(value) {
  const text = String(value == null ? '' : value);
  return text.length >= COMPLETED_CHAT_TEXT_FINGERPRINT_MIN_CHARS ? text : '';
}

export function completedChatIdentitiesFromMessages(messages = [], currentMessage = null) {
  const completed = (Array.isArray(messages) ? messages : []).filter(message => (
    message
    && message !== currentMessage
    && message.sender === 'bot'
    && message.isLoading !== true
    && String(message.text || '').trim()
  ));
  const lastCompleted = completed.length ? completed[completed.length - 1] : null;
  return {
    runIds: completed
      .map(message => normalizeChatRunId(message && message.run_id))
      .filter(Boolean),
    responseMessageIds: completed.flatMap(message => [
      safePart(message && message.message_id),
      safePart(message && message.response_message_id),
    ]).filter(Boolean),
    texts: completed
      .map(message => completedChatTextFingerprint(message && message.text))
      .filter(Boolean),
    lastCompletedRunId: normalizeChatRunId(lastCompleted && lastCompleted.run_id),
  };
}

/**
 * True when a new in-flight placeholder must ignore this chat/status event.
 * Otherwise a late chunk from the previous answer can paint this empty bubble
 * while the new run is still reasoning.
 */
export function isStaleInFlightChatUpdate({
  incomingRunId,
  reservedRunId,
  lastCompletedRunId,
  incomingResponseMessageId,
  reservedResponseMessageId,
  completedResponseMessageIds = [],
  completedRunIds = [],
  completedMessageTexts = [],
  incomingText = '',
} = {}) {
  const incoming = normalizeChatRunId(incomingRunId);
  const reserved = normalizeChatRunId(reservedRunId);
  const incomingResponseId = safePart(incomingResponseMessageId);
  const rawReservedResponseId = safePart(reservedResponseMessageId);
  // Optimistic assistant bubbles receive a client-only DOM identity before
  // the server accepts the request. Do not bind that presentation id as the
  // durable response identity; the first authoritative event must be able to
  // supply it.
  const reservedResponseId = durableChatResponseId(rawReservedResponseId);
  const reservedResponseIsProvisional = !!(
    rawReservedResponseId && !reservedResponseId
  );
  const completedIds = Array.isArray(completedResponseMessageIds)
    ? completedResponseMessageIds.map(safePart).filter(Boolean)
    : [];
  const completedRuns = Array.isArray(completedRunIds)
    ? completedRunIds.map(normalizeChatRunId).filter(Boolean)
    : [];
  const completedTexts = Array.isArray(completedMessageTexts)
    ? completedMessageTexts.map(completedChatTextFingerprint).filter(Boolean)
    : [];
  const incomingFingerprint = completedChatTextFingerprint(incomingText);
  const boundToReservedRun = !!(reserved && incoming && incoming === reserved);
  const boundToReservedResponse = !!(
    incomingResponseId
    && incomingResponseId === reservedResponseId
  );

  if (chatRunIdIsAtMost(incoming, lastCompletedRunId)) return true;
  if (incoming && completedRuns.includes(incoming) && incoming !== reserved) return true;

  // A provisional DOM id is not enough to select this bubble. Require the
  // first durable response identity to arrive on its already reserved run;
  // otherwise a late off-page event could claim the current placeholder.
  if (reservedResponseIsProvisional && incomingResponseId && !boundToReservedRun) {
    return true;
  }

  if (
    incomingResponseId
    && completedIds.includes(incomingResponseId)
    && incomingResponseId !== reservedResponseId
  ) {
    return true;
  }

  if (
    incomingFingerprint
    && completedTexts.includes(incomingFingerprint)
    && !boundToReservedRun
    && !boundToReservedResponse
  ) {
    return true;
  }

  if (reserved && incoming && incoming !== reserved) {
    const reservedNumeric = Number(reserved);
    const incomingNumeric = Number(incoming);
    if (Number.isFinite(reservedNumeric) && Number.isFinite(incomingNumeric)) {
      return incomingNumeric < reservedNumeric;
    }
  }

  if (
    reservedResponseId
    && incomingResponseId
    && incomingResponseId !== reservedResponseId
  ) {
    return true;
  }

  return false;
}

export function shouldHydrateInFlightChatText({
  placeholderText,
  snapshotText,
  snapshotDone = false,
  incomingRunId,
  reservedRunId,
  lastCompletedRunId,
  incomingResponseMessageId,
  reservedResponseMessageId,
  completedResponseMessageIds,
  completedRunIds,
  completedMessageTexts,
  incomingText,
} = {}) {
  const text = typeof snapshotText === 'string' ? snapshotText : '';
  if (!text) return false;
  if (isStaleInFlightChatUpdate({
    incomingRunId,
    reservedRunId,
    lastCompletedRunId,
    incomingResponseMessageId,
    reservedResponseMessageId,
    completedResponseMessageIds,
    completedRunIds,
    completedMessageTexts,
    incomingText: incomingText === undefined ? text : incomingText,
  })) {
    return false;
  }
  const placeholderIsEmpty = !String(placeholderText || '');
  if (placeholderIsEmpty && snapshotDone) return false;
  const reserved = normalizeChatRunId(reservedRunId);
  const incoming = normalizeChatRunId(incomingRunId);
  if (placeholderIsEmpty && reserved && incoming && incoming !== reserved) return false;
  return true;
}

export function shouldDeferEmptySequencedTerminal(data) {
  if (!data || data.sequence === undefined || data.sequence === null) return false;
  const isTerminal = data.done === true || data.is_complete === true;
  if (!isTerminal || data.cancelled === true) return false;
  return typeof data.text !== 'string' || data.text.length === 0;
}

export function findChatRequestContextForMessage(contexts, message) {
  const active = (Array.isArray(contexts) ? contexts : []).filter(context => (
    context && context.cancelled !== true && context.completed !== true
  ));
  if (!message) return null;

  const identityMatch = active.find(context => context.loadingMessage === message);
  if (identityMatch) return identityMatch;

  const responseId = safePart(message.response_message_id || message.message_id);
  if (responseId) {
    const matches = active.filter(context => safePart(context.responseMessageId) === responseId);
    if (matches.length === 1) return matches[0];
  }

  const runId = safePart(message.run_id);
  if (runId) {
    const matches = active.filter(context => safePart(context.runId) === runId);
    if (matches.length === 1) return matches[0];
  }
  return null;
}

export async function writeClipboardContent({
  clipboard,
  ClipboardItemClass,
  BlobClass,
  html,
  plaintext,
}) {
  const plainValue = String(plaintext == null ? '' : plaintext);
  if (
    ClipboardItemClass
    && BlobClass
    && clipboard
    && typeof clipboard.write === 'function'
  ) {
    try {
      await clipboard.write([new ClipboardItemClass({
        'text/html': new BlobClass([String(html == null ? '' : html)], { type: 'text/html' }),
        'text/plain': new BlobClass([plainValue], { type: 'text/plain' }),
      })]);
      return 'rich';
    } catch (richError) {
      if (!clipboard || typeof clipboard.writeText !== 'function') throw richError;
    }
  }
  if (clipboard && typeof clipboard.writeText === 'function') {
    await clipboard.writeText(plainValue);
    return 'text';
  }
  throw new Error('clipboard_unavailable');
}

export function mergeVersionedToolReceipts(existingReceipts, incomingReceipts) {
  const byId = new Map();
  (Array.isArray(existingReceipts) ? existingReceipts : []).forEach((receipt) => {
    const toolRunId = safePart(receipt && receipt.tool_run_id);
    if (toolRunId) byId.set(toolRunId, receipt);
  });
  (Array.isArray(incomingReceipts) ? incomingReceipts : []).forEach((receipt) => {
    const toolRunId = safePart(receipt && receipt.tool_run_id);
    if (!toolRunId) return;
    const previous = byId.get(toolRunId);
    const previousVersion = Number(previous && previous.state_version || 0);
    const incomingVersion = Number(receipt && receipt.state_version || 0);
    if (previous && previousVersion > incomingVersion) return;
    byId.set(toolRunId, receipt);
  });
  return Array.from(byId.values()).sort((left, right) => (
    Number(left && left.event_seq || 0) - Number(right && right.event_seq || 0)
  ));
}

export function toolReceiptVersion(source, entityId) {
  const id = String(entityId || '').trim();
  if (!id) return 0;
  if (Array.isArray(source)) {
    let version = 0;
    source.forEach((receipt) => {
      if (String(receipt && receipt.tool_run_id || '') !== id) return;
      version = Math.max(version, Number.parseInt(String(receipt && receipt.state_version || 0), 10) || 0);
    });
    return version;
  }
  if (!source || typeof source !== 'object') return 0;
  return Number.parseInt(String(source[id] || 0), 10) || 0;
}

export function recordToolReceiptVersions(target, receipts) {
  const next = target && typeof target === 'object' ? target : {};
  (Array.isArray(receipts) ? receipts : []).forEach((receipt) => {
    const id = String(receipt && receipt.tool_run_id || '').trim();
    const version = Number.parseInt(String(receipt && receipt.state_version || 0), 10) || 0;
    if (!id || version <= 0) return;
    next[id] = Math.max(Number(next[id] || 0), version);
  });
  return next;
}

export function hasUnobservedToolReceiptVersions(expected, ...sources) {
  if (!expected || typeof expected !== 'object') return false;
  const seen = {};
  sources.forEach((source) => {
    if (Array.isArray(source)) {
      recordToolReceiptVersions(seen, source);
      return;
    }
    Object.entries(source && typeof source === 'object' ? source : {}).forEach(([id, version]) => {
      const key = String(id || '').trim();
      const value = Number.parseInt(String(version || 0), 10) || 0;
      if (!key || value <= 0) return;
      seen[key] = Math.max(Number(seen[key] || 0), value);
    });
  });
  let missing = false;
  Object.entries(expected).forEach(([entityId, version]) => {
    if (Number(seen[entityId] || 0) >= Number(version || 0)) {
      delete expected[entityId];
    } else {
      missing = true;
    }
  });
  return missing;
}

export function shouldInterceptReferenceClick(event) {
  if (!event) return true;
  if (event.defaultPrevented) return false;
  if (Number(event.button || 0) !== 0) return false;
  return !(event.metaKey || event.ctrlKey || event.shiftKey || event.altKey);
}

const canonicalMessageRole = message => safePart(
  message && (message.sender || message.role),
).toLowerCase();

const canonicalMessageHasText = message => !!safePart(
  message && (message.text || message.content || message.message),
);

const isOrdinaryCanonicalAiTrigger = message => !!(
  message
  && canonicalMessageRole(message) === 'user'
  && !isTrueFlag(message.human_only)
  && !isTrueFlag(message.ai_queued)
  && !safePart(message.ai_queue_status)
  && !isTrueFlag(message.chat_document_injection)
  && !isTrueFlag(message.document_injection)
  && !isTrueFlag(message.chat_context_resume_message)
  && !isTrueFlag(message.context_resume_message)
  && !isTrueFlag(message.context_resume)
);

const isCompletedCanonicalAssistant = message => {
  if (
    !message
    || !['assistant', 'bot'].includes(canonicalMessageRole(message))
    || !canonicalMessageHasText(message)
  ) return false;
  const status = safePart(message.status).toLowerCase();
  const phase = safePart(message.phase).toLowerCase();
  if (['loading', 'running', 'in_progress', 'failed', 'error', 'cancelled', 'canceled', 'waiting_user'].includes(status)) return false;
  if (['loading', 'running', 'in_progress', 'failed', 'error', 'cancelled', 'canceled', 'waiting_user'].includes(phase)) return false;
  return !(
    isTrueFlag(message.isLoading)
    || isTrueFlag(message.is_loading)
    || isTrueFlag(message.loading)
    || isTrueFlag(message.failed)
    || isTrueFlag(message.error)
    || isTrueFlag(message.cancelled)
    || isTrueFlag(message.canceled)
    || isTrueFlag(message.was_cancelled)
    || isTrueFlag(message.waitingForUser)
    || isTrueFlag(message.waiting_for_user)
    || isTrueFlag(message.continuationNeedsAction)
    || isTrueFlag(message.resumingContext)
    || message.done === false
    || message.is_complete === false
    || message.completed === false
  );
};

const canonicalPageIncludesTail = pagination => {
  if (!pagination || typeof pagination !== 'object') return true;
  if (isTrueFlag(pagination.has_next)) return false;
  if (!isTrueFlag(pagination.enabled)) return true;
  const end = Number(pagination.end);
  const total = Number(pagination.total);
  return Number.isFinite(end) && Number.isFinite(total) && end >= total;
};

const durableCanonicalTriggerIdentity = message => ({
  messageIds: new Set([
    safePart(message && message.message_id),
    safePart(message && message.client_message_id),
  ].filter(Boolean)),
  turnId: safePart(message && message.turn_id),
  clientRequestId: safePart(message && message.client_request_id),
});

const canonicalAssistantAncestry = (assistant, triggerIdentity) => {
  const directTriggerIds = Array.from(new Set([
    safePart(assistant && assistant.trigger_message_id),
    safePart(assistant && assistant.user_message_id),
  ].filter(Boolean)));
  const anchorMessageIds = Array.from(new Set([
    safePart(assistant && assistant.ai_response_anchor_message_id),
    safePart(assistant && assistant.parent_message_id),
  ].filter(Boolean)));
  const assistantTurnId = safePart(assistant && assistant.turn_id);
  const assistantRequestId = safePart(assistant && assistant.client_request_id);
  const hasDurableAncestry = !!(
    directTriggerIds.length
    || anchorMessageIds.length
    || assistantTurnId
    || assistantRequestId
  );
  // A shared turn must not override an explicitly contradictory direct
  // trigger or user-message identity.
  if (directTriggerIds.length && triggerIdentity.messageIds.size) {
    const directIdsMatch = directTriggerIds.every(identity => (
      triggerIdentity.messageIds.has(identity)
    ));
    if (!directIdsMatch) return { hasDurableAncestry, matches: false };
  }
  // A continuation's visible resume note can become its response anchor while
  // retaining the original turn. Shared turn identity is therefore the
  // strongest ancestry signal and must be evaluated before message anchors.
  if (assistantTurnId && triggerIdentity.turnId) {
    return {
      hasDurableAncestry,
      matches: assistantTurnId === triggerIdentity.turnId,
    };
  }
  const comparableMessageIds = [...directTriggerIds, ...anchorMessageIds];
  if (comparableMessageIds.length && triggerIdentity.messageIds.size) {
    return {
      hasDurableAncestry,
      matches: comparableMessageIds.every(identity => (
        triggerIdentity.messageIds.has(identity)
      )),
    };
  }
  if (assistantRequestId && triggerIdentity.clientRequestId) {
    return {
      hasDurableAncestry,
      matches: assistantRequestId === triggerIdentity.clientRequestId,
    };
  }
  return {
    hasDurableAncestry,
    matches: false,
  };
};

/**
 * Whether a canonical transcript proves that its latest ordinary user turn
 * has a settled assistant answer. Durable ancestry is authoritative; ordered
 * pairing is retained only for legacy rows that have no correlation IDs.
 */
export function canonicalChatProvesLatestTurnCompleted({
  messages = [],
  inProgress = false,
  hasRecoverableGenerationError = false,
  pagination = null,
} = {}) {
  if (
    !Array.isArray(messages)
    || isTrueFlag(inProgress)
    || isTrueFlag(hasRecoverableGenerationError)
    || !canonicalPageIncludesTail(pagination)
  ) return false;

  let triggerIndex = -1;
  for (let index = messages.length - 1; index >= 0; index -= 1) {
    if (isOrdinaryCanonicalAiTrigger(messages[index])) {
      triggerIndex = index;
      break;
    }
  }
  if (triggerIndex < 0) return false;

  const assistants = messages
    .slice(triggerIndex + 1)
    .filter(message => ['assistant', 'bot'].includes(canonicalMessageRole(message)));
  if (!assistants.length) return false;

  // The newest assistant attempt owns the terminal state. An older completed
  // answer cannot prove completion while a later attempt remains unsettled.
  const latestAssistant = assistants[assistants.length - 1];
  if (!isCompletedCanonicalAssistant(latestAssistant)) return false;

  const triggerIdentity = durableCanonicalTriggerIdentity(messages[triggerIndex]);
  const triggerHasDurableIdentity = !!(
    triggerIdentity.messageIds.size
    || triggerIdentity.turnId
    || triggerIdentity.clientRequestId
  );
  const ancestry = canonicalAssistantAncestry(latestAssistant, triggerIdentity);
  if (ancestry.matches) return true;
  if (triggerHasDurableIdentity || ancestry.hasDurableAncestry) {
    return false;
  }
  return true;
}

const isRunningQueuedAiMessage = message => !!(
  message
  && message.sender === 'user'
  && message.ai_queued === true
  && safePart(message.ai_queue_status).toLowerCase() === 'running'
);

const isOrdinaryAiTriggerMessage = message => !!(
  message
  && message.sender === 'user'
  && message.human_only !== true
  && message.ai_queued !== true
);

const isExactTerminalTriggerMessage = message => !!(
  isOrdinaryAiTriggerMessage(message)
  || isRunningQueuedAiMessage(message)
);

const terminalTriggerIndex = (messages, terminalIndex) => {
  const terminal = messages[terminalIndex] || {};
  const durableTriggerIds = Array.from(new Set([
    safePart(terminal.trigger_message_id),
    safePart(terminal.ai_response_anchor_message_id),
  ].filter(Boolean)));
  if (durableTriggerIds.length) {
    let matchedIndex = -1;
    for (const triggerId of durableTriggerIds) {
      let candidateIndex = -1;
      for (let index = 0; index < messages.length; index += 1) {
        const candidate = messages[index];
        if (
          candidate
          && candidate.sender === 'user'
          && safePart(candidate.message_id) === triggerId
        ) {
          candidateIndex = index;
          break;
        }
      }
      // Durable ancestry is authoritative. An absent page/window ancestor, a
      // conflicting pair of IDs, or a non-running queued message must never be
      // replaced by a nearest-user guess.
      if (candidateIndex < 0 || (matchedIndex >= 0 && matchedIndex !== candidateIndex)) {
        return { index: -1, durable: true, matched: false };
      }
      matchedIndex = candidateIndex;
    }
    return {
      index: matchedIndex,
      durable: true,
      matched: matchedIndex >= 0 && isExactTerminalTriggerMessage(messages[matchedIndex]),
    };
  }

  // Older local conversations do not have durable message IDs. Preserve their
  // retry affordance only when the nearest AI-triggering user turn is
  // unambiguous; collaborative human-only messages must never become AI input.
  for (let index = terminalIndex - 1; index >= 0; index -= 1) {
    const candidate = messages[index];
    if (isOrdinaryAiTriggerMessage(candidate)) {
      return { index, durable: false, matched: true };
    }
  }
  return { index: -1, durable: false, matched: false };
};

export function resolveTerminalActionState(rawMessages, hasChatError = false) {
  const messages = Array.isArray(rawMessages) ? rawMessages : [];
  let latestUserIndex = -1;
  let latestBotIndex = -1;
  for (let index = messages.length - 1; index >= 0; index -= 1) {
    const message = messages[index];
    if (latestUserIndex < 0 && message && message.sender === 'user') latestUserIndex = index;
    if (latestBotIndex < 0 && message && message.sender === 'bot') latestBotIndex = index;
    if (latestUserIndex >= 0 && latestBotIndex >= 0) break;
  }

  const latestBot = latestBotIndex >= 0 ? messages[latestBotIndex] : null;
  const latestBotIsTerminal = !!(
    latestBot
    && (latestBot.failed === true || latestBot.cancelled === true)
  );
  if (latestBotIsTerminal) {
    const trigger = terminalTriggerIndex(messages, latestBotIndex);
    const triggerIndex = trigger.index;
    if (trigger.durable && !trigger.matched) {
      return Object.freeze({ mode: 'none', terminalIndex: latestBotIndex, triggerIndex: -1 });
    }
    // Retry/edit handlers intentionally target the latest user message. They
    // are safe only when that user is the terminal bubble's own AI trigger.
    // A later collaborative human message therefore suppresses both inline and
    // legacy fallback controls instead of accidentally sending it to the AI.
    if (triggerIndex >= 0 && triggerIndex === latestUserIndex) {
      return Object.freeze({
        mode: 'inline',
        terminalIndex: latestBotIndex,
        triggerIndex,
      });
    }
    const latestUser = latestUserIndex >= 0 ? messages[latestUserIndex] : null;
    if (
      hasChatError === true
      && latestUserIndex > triggerIndex
      && isOrdinaryAiTriggerMessage(latestUser)
    ) {
      return Object.freeze({ mode: 'fallback', terminalIndex: latestBotIndex, triggerIndex: latestUserIndex });
    }
    return Object.freeze({ mode: 'none', terminalIndex: latestBotIndex, triggerIndex });
  }

  const latestUser = latestUserIndex >= 0 ? messages[latestUserIndex] : null;
  if (
    hasChatError === true
    && latestUserIndex > latestBotIndex
    && isOrdinaryAiTriggerMessage(latestUser)
  ) {
    return Object.freeze({ mode: 'fallback', terminalIndex: -1, triggerIndex: latestUserIndex });
  }
  return Object.freeze({ mode: 'none', terminalIndex: -1, triggerIndex: -1 });
}

export function toolMutationIdentity(chatUuid, receipt) {
  const resultRef = receipt && receipt.result_ref && typeof receipt.result_ref === 'object'
    ? receipt.result_ref
    : {};
  return Object.freeze({
    chatUuid: safePart(chatUuid),
    toolRunId: safePart(receipt && receipt.tool_run_id),
    resourceType: safePart(resultRef.type),
    resourceId: safePart(resultRef.id),
  });
}

export function sameToolMutationIdentity(left, right) {
  return !!(
    left
    && right
    && left.chatUuid === right.chatUuid
    && left.toolRunId === right.toolRunId
    && left.resourceType === right.resourceType
    && left.resourceId === right.resourceId
  );
}

export function toolOperationStorageKey(identity, scope, detail = '') {
  const value = identity || {};
  return [
    safePart(value.chatUuid),
    safePart(value.toolRunId),
    safePart(value.resourceType),
    safePart(value.resourceId) || 'resource',
    safePart(scope),
    safePart(detail),
  ].join('|');
}

export function artifactProjectionReceiptKey(chatUuid, receipt) {
  const resultRef = receipt && receipt.result_ref && typeof receipt.result_ref === 'object'
    ? receipt.result_ref
    : {};
  return [
    safePart(chatUuid),
    safePart(receipt && receipt.tool_run_id),
    safePart(resultRef.type),
    safePart(resultRef.id),
  ].join('|');
}
